/**
 * TenonLogo / TenonGlyph — brand marks for hub.usetenon.com.
 *
 * Inline-SVG ports of ~/Downloads/branding/icons/{wordmark-light,
 * wordmark-dark,glyph-mono}.svg per BRAND-ICONS-HUB. Inlined (vs <img
 * src>) so the glyph picks up `currentColor` and the wordmarks ship in
 * a single network request with the bundle.
 *
 * <TenonLogo /> — full wordmark. Resolves dark/light variant from the
 * HubThemeProvider (data-theme on the .tenon-hub root), so it follows
 * the user's theme toggle without a remount.
 *
 * <TenonGlyph /> — square 64x64 monogram, fill via `currentColor`.
 * Reserved for sidebar collapsed-state + small contexts; sized via
 * the `size` prop (CSS px).
 */
import React, { useContext } from 'react'
import { ThemeCtx, type HubTheme } from './ThemeContext'

type LogoProps = {
  /** target height in CSS px; width follows the 240:64 viewBox ratio */
  height?: number
  /** override the theme-driven palette; defaults to current theme, then 'dark' */
  variant?: HubTheme
  className?: string
  style?: React.CSSProperties
  title?: string
}

export function TenonLogo({
  height = 22, variant, className, style, title = 'Tenon',
}: LogoProps) {
  const ctx = useContext(ThemeCtx)
  const v = variant ?? ctx?.theme ?? 'dark'
  // wordmark-{dark,light}.svg — wordmark-dark.svg renders the cream
  // accent (#EFF890) + light text (#F1F2F4) for dark backgrounds; the
  // light file uses gold (#C99E35) + ink text for light backgrounds.
  const accent = v === 'dark' ? '#EFF890' : '#C99E35'
  const text = v === 'dark' ? '#F1F2F4' : '#0E0F10'
  return (
    <svg
      role="img"
      aria-label={title}
      className={className}
      style={{ height, width: 'auto', display: 'block', ...style }}
      viewBox="0 0 240 64"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <rect
        x="6" y="14" width="36" height="36" rx="2"
        fill="none" stroke={accent} strokeWidth="3" strokeLinejoin="round"
      />
      <rect x="32" y="22" width="26" height="20" fill={accent} />
      <line x1="42" y1="22" x2="42" y2="42" stroke="#0E0F10" strokeWidth="1.5" />
      <text
        x="76" y="44"
        fontFamily="Karla, system-ui, sans-serif"
        fontWeight="700" fontSize="36"
        fill={text}
        letterSpacing="-0.04em"
      >
        Tenon
      </text>
    </svg>
  )
}

type GlyphProps = {
  size?: number
  className?: string
  style?: React.CSSProperties
  title?: string
}

export function TenonGlyph({
  size = 22, className, style, title = 'Tenon',
}: GlyphProps) {
  return (
    <svg
      role="img"
      aria-label={title}
      className={className}
      style={{ width: size, height: size, display: 'block', color: 'inherit', ...style }}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <rect
        x="6" y="14" width="36" height="36" rx="2"
        fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinejoin="round"
      />
      <rect x="32" y="22" width="10" height="20" fill="currentColor" />
      <rect x="44" y="22" width="14" height="20" fill="currentColor" />
    </svg>
  )
}
